import { combineEpics } from 'redux-observable';
import { Observable } from 'rxjs/Observable';
import get from 'lodash/get';
import qs from 'query-string';

import { fetchSingleCard } from '../utils/api';
import { queryCompare } from '../utils/urls/urlComparer';
import { SingleCardApi } from '../parsers';

import {
  SINGLE_CARD_FETCH_REQUESTED,
  SINGLE_CARD_ORIGINAL_FETCH_REQUESTED,
  singleCardFetched,
  singleCardVariationFetched,
  singleCardOriginalFetched,
  singleCardFailed,
} from '../redux/singleCard';

import type { ActionObs, Store, Obs } from '../types';

// This epic is used for the current visible card in the details view
export function singleCardEpic(action$: ActionObs, store: Store): Obs {
  return action$
    .ofType(SINGLE_CARD_FETCH_REQUESTED)
    .switchMap(({ payload: { cardId, variationId, suiteVariationId } }) => {
      const { route, env, packages, singleCard } = store.getState();
      // When fetching the single card pass along what the users context was
      const currentRoute = get(route, 'currentRoute');
      const previousRoute = get(route, 'previousRoute', {});
      const currency = get(env, 'currency', 'USD');
      let category = get(packages, 'navNode.slug', undefined);
      category =
        !category || category.length < 1
          ? get(singleCard, 'slug', '')
          : category;

      let nav = {};
      nav[currentRoute.pathname.split('/')[1]] = currentRoute.slug;

      let changes = {
        card: cardId,
      };
      if (variationId && variationId.length !== 0) {
        changes.variation_id = variationId;
      }
      if (suiteVariationId && suiteVariationId.length !== 0) {
        changes.suite_variation_id = suiteVariationId;
      }

      const propDifferences = queryCompare(changes, previousRoute.query);
      const hasVariationDiff = propDifferences.indexOf('variation_id') > -1;
      const hasSuiteVariationDiff =
        propDifferences.indexOf('suite_variation_id') > -1;

      // The current visible card
      let currentId = cardId;
      if (
        hasSuiteVariationDiff &&
        suiteVariationId &&
        suiteVariationId.length > 0
      ) {
        currentId = suiteVariationId;
      } else if (hasVariationDiff && variationId && variationId.length > 0) {
        currentId = variationId;
      }

      return fetchSingleCard({ cardId: currentId, currency, category, ...nav })
        .map(resp => SingleCardApi.parse(resp))
        .map(({ singleCard, variations }) => {
          // Only update variations if we variations looking at the main card
          return variationId || suiteVariationId
            ? singleCardVariationFetched(singleCard)
            : singleCardFetched(singleCard, variations);
        })
        .catch(err => Observable.of(singleCardFailed(err.message)));
    });
}

// This epics primary purpose is for loading the varations of the main card
// Normally this epic would fire if a user was deeplinked to a varation
// ie: /cards/card/:id?variation_id=123
// Alternative to this could be we redirect to /cards/card/123 in this scenario
export function singleCardOriginalDataEpic(
  action$: ActionObs,
  store: Store,
): Obs {
  return action$
    .ofType(SINGLE_CARD_ORIGINAL_FETCH_REQUESTED)
    .switchMap(({ payload: { cardId } }) => {
      const { singleCard, env, packages } = store.getState();
      // When fetching the single card pass along what the users context was
      const currentOriginalId = get(singleCard, 'originalData.id', -1);

      if (currentOriginalId === cardId) {
        return Observable.empty();
      }

      const currency = get(env, 'currency', 'USD');
      let category = get(packages, 'navNode.slug', undefined);
      category =
        !category || category.length < 1
          ? get(singleCard, 'slug', '')
          : category;

      return fetchSingleCard({ cardId, currency, category })
        .map(resp => SingleCardApi.parse(resp))
        .map(({ singleCard, variations }) => {
          return singleCardOriginalFetched(singleCard, variations);
        })
        .catch(err => Observable.of(singleCardFailed(err)));
    });
}

export default combineEpics(singleCardOriginalDataEpic, singleCardEpic);
